import React from 'react';

import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';
import { getRegionCodes } from '@luxuryescapes/lib-regions';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PromoCodeNameField from '~/components/Promo/PromoCodeNameField';

import { CheckPromoDisplayParams } from '~/hooks/usePromoDisplayCheck';

import { displayConditionType } from '~/services/PromoService';

import dateFormatter from '~/utils/dateFormatter';

import CopyableField from '../Common/CopyableField';
import DebugModal from '../DebugModal/DebugModal';
import PromoDisplayConfigLink from '../Promo/PromoDisplayConfigLink';
import { promoCodePath } from '../Promo/promoUrls';

import PromoDisplayConfigForm from './PromoDisplayConfigForm';
import PromoDisplayConfigWarningsCell from './PromoDisplayConfigWarningsCell';

const buildDisplayConfigUrl = (row: definitions['PromoDisplayConfig']) => {
  if (!row) {
    return '';
  }
  const urlParams = new URLSearchParams();
  if (typeof row.source === 'string' && row.source.trim()) {
    urlParams.set('utm_source', row.source.trim());
  }
  if (typeof row.medium === 'string' && row.medium.trim()) {
    urlParams.set('utm_medium', row.medium.trim());
  }
  if (typeof row.campaign === 'string' && row.campaign.trim()) {
    urlParams.set('utm_campaign', row.campaign.trim());
  }
  const qs = urlParams.toString();
  const host = window.configs['LUX_CUSTOMER_PORTAL'];
  const cleanUrlPath = row.url_path ? row.url_path.replace('*', '') : '';
  const fullPath = cleanUrlPath ? `${host.replace('/au', '')}${cleanUrlPath}` : host;
  const url = `${fullPath}?${qs}`;
  return url;
};

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getColumns = ({
  handleExpirePromoConfig,
  handleEditPromoDisplayConfig,
  handleClonePromoDisplayConfig,
  enqueueSnackbar,
  handlePromoDisplayNavigate,
}: {
  handleExpirePromoConfig: ({ id }: { id: string }) => Promise<void>;
  handleEditPromoDisplayConfig: (config: any) => void;
  handleClonePromoDisplayConfig: (config: any) => void;
  enqueueSnackbar: (message: string, options?: any) => void;
  handlePromoDisplayNavigate: (params: CheckPromoDisplayParams) => void;
}): Array<GridColDef<definitions['PromoDisplayConfig']>> => [
  {
    field: 'promo_display_config_id',
    headerName: 'ID',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Grid container spacing={1}>
          <Grid>
            <CopyableField value={params.row.promo_display_config_id} />
          </Grid>
          {params?.row?.rule_json?.displayOptions?.map((displayOption, index) => (
            <Grid key={index}>
              <Typography sx={{ fontSize: '0.75rem' }}>
                {displayOption.displayOptionKey}
                {displayOption.chanceOfDisplay ? ` (${displayOption.chanceOfDisplay}%)` : ''}
              </Typography>
            </Grid>
          ))}
        </Grid>
      );
    },
  },
  {
    field: 'code_name',
    headerName: 'Promo Code Name',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Link to={promoCodePath(params.row.code_name)} target="_blank" rel="noreferrer">
          {params.row.code_name}
        </Link>
      );
    },
  },
  {
    field: 'warnings',
    headerName: 'Warnings',
    ...sharedOpts,
    minWidth: 220,
    flex: 1,
    renderCell: (params) => <PromoDisplayConfigWarningsCell codeName={params.row.code_name} brand={params.row.brand} />,
  },
  {
    field: 'region',
    headerName: 'Region',
    ...sharedOpts,
    flex: 0.3,
  },
  {
    field: 'url_dfgtfpath',
    headerName: 'Display Conditions',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Grid container direction="column" spacing={1}>
          <Grid>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {params.row.url_path}
            </Typography>
          </Grid>
          {params?.row?.rule_json?.conditions && params.row.rule_json.conditions.length > 0 ? (
            params.row.rule_json.conditions.map((condition, index) => (
              <Grid key={index}>
                <Typography sx={{ fontSize: '0.75rem' }}>
                  <b>{condition.field}:</b> {displayConditionType(condition.conditionType)} {condition.value}
                </Typography>
              </Grid>
            ))
          ) : (
            <Grid>
              <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                No conditions
              </Typography>
            </Grid>
          )}
        </Grid>
      );
    },
  },
  {
    field: 'utm_params',
    headerName: 'UTM Params',
    renderCell: (params) => {
      return (
        <Stack direction="column" alignItems="flex-start">
          <Stack>
            <Typography>
              <b>Source:</b> {params.row.source}
            </Typography>
            <Typography>
              <b>Medium:</b> {params.row.medium}
            </Typography>
            <Typography>
              <b>Campaign:</b> {params.row.campaign}
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              const displayConfigUrl = buildDisplayConfigUrl(params.row);
              if (!displayConfigUrl) {
                enqueueSnackbar('displayConfigUrl could not be built', { variant: 'error' });
                return;
              }
              try {
                await navigator.clipboard.writeText(displayConfigUrl);
                enqueueSnackbar('UTM params copied to clipboard', { variant: 'success' });
              } catch (error) {
                enqueueSnackbar('Failed to copy to clipboard ' + (error.message || 'Unknown error'), {
                  variant: 'error',
                });
              }
            }}
          >
            Link With Params
          </Button>
        </Stack>
      );
    },
    ...sharedOpts,
    flex: 1.5,
  },
  {
    field: 'status',
    headerName: 'Status',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Typography
          sx={{
            color: params.row.status === 'active' ? 'success.main' : 'error.main',
            fontWeight: 'bold',
          }}
        >
          {params.row.status}
        </Typography>
      );
    },
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => {
      return <Typography>{dateFormatter(params.row.created_at)}</Typography>;
    },
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => {
      return <Typography>{dateFormatter(params.row.updated_at)}</Typography>;
    },
  },
  {
    field: 'links',
    headerName: 'Links',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            height: '100%',
            lineHeight: 'normal',
            minHeight: '120px',
            paddingTop: 1,
          }}
        >
          <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
            <Button variant="outlined" size="small" onClick={() => handleEditPromoDisplayConfig(params.row)}>
              Edit
            </Button>
            <PromoDisplayConfigLink promoDisplayConfigId={params.row.promo_display_config_id} type="check" />
            <PromoDisplayConfigLink promoDisplayConfigId={params.row.promo_display_config_id} type="results" />
            <DebugModal type="generic" data={params.row} />
          </Stack>
        </Box>
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Stack direction="column" spacing={1}>
          <Button variant="contained" size="small" onClick={() => handleClonePromoDisplayConfig(params.row)}>
            Clone
          </Button>

          {params.row.status === 'active' && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleExpirePromoConfig({ id: params.row.promo_display_config_id })}
            >
              Expire
            </Button>
          )}
        </Stack>
      );
    },
  },
];

type PromoDisplayConfigTabProps = {
  // Search filters
  promoCodeName: string;
  setPromoCodeName: (value: string) => void;
  urlPath: string;
  setUrlPath: (value: string) => void;
  region: any;
  setRegion: (value: any) => void;
  source: string;
  setSource: (value: string) => void;
  medium: string;
  setMedium: (value: string) => void;
  campaign: string;
  setCampaign: (value: string) => void;
  status: any;
  setStatus: (value: any) => void;

  // Data and loading states
  data: Array<any>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;

  // Handlers
  onPageChange: (page: number, pageSize: number) => void;
  handleStatusChange: (event: any) => void;
  handleOpen: () => void;
  handleResultsOpen: (promoDisplayConfigId?: string) => void;
  handleExpirePromoConfigWithFetch: ({ id }: { id: string }) => Promise<void>;
  handleEditPromoDisplayConfig: (config: any) => void;
  handleClonePromoDisplayConfig: (config: any) => void;
  handlePromoDisplayNavigate: (params: CheckPromoDisplayParams) => void;

  // Form state
  open: boolean;
  setOpen: (open: boolean) => void;
  editableConfig: any;
  fetchData: () => void;
};

export default function PromoDisplayConfigTab({
  promoCodeName,
  setPromoCodeName,
  urlPath,
  setUrlPath,
  region,
  setRegion,
  source,
  setSource,
  medium,
  setMedium,
  campaign,
  setCampaign,
  status,
  setStatus,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  handleStatusChange,
  handleOpen,
  handleResultsOpen,
  handleExpirePromoConfigWithFetch,
  handleEditPromoDisplayConfig,
  handleClonePromoDisplayConfig,
  handlePromoDisplayNavigate,
  open,
  setOpen,
  editableConfig,
  fetchData,
}: PromoDisplayConfigTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const dataRows = data.map((row, index) => ({
    id: index,
    ...row,
  }));

  return (
    <>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <PromoCodeNameField
            id="code_name"
            label="Promo Code Name"
            variant="outlined"
            value={promoCodeName}
            onChange={(e) => setPromoCodeName(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <TextField
            id="url_path"
            label="URL Path"
            variant="outlined"
            value={urlPath}
            onChange={(e) => setUrlPath(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Autocomplete
            id="region"
            options={getRegionCodes()}
            onChange={(_, value) => setRegion((value as any) ?? 'AU')}
            renderInput={(params) => <TextField {...params} label="Region" />}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Tooltip title="Use special value 'ALL' to search for all sources, mediums, and campaigns" placement="bottom">
            <TextField
              id="source"
              label="Source"
              variant="outlined"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              fullWidth
            />
          </Tooltip>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Tooltip title="Use special value 'ALL' to search for all sources, mediums, and campaigns" placement="bottom">
            <TextField
              id="medium"
              label="Medium"
              variant="outlined"
              value={medium}
              onChange={(e) => setMedium(e.target.value)}
              fullWidth
            />
          </Tooltip>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Tooltip title="Use special value 'ALL' to search for all sources, mediums, and campaigns" placement="bottom">
            <TextField
              id="campaign"
              label="Campaign"
              variant="outlined"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              fullWidth
            />
          </Tooltip>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status-label">Status</InputLabel>
            <Select labelId="status-label" value={status} label="Status" onChange={handleStatusChange}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid
          size={{
            xs: 12,
          }}
        >
          <Box mt={2} gap={4} display="flex" justifyContent="space-between" alignItems="center">
            <Button variant="contained" color="primary">
              Search
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleResultsOpen(editableConfig?.promo_display_config_id)}
            >
              Results
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Promo Display Config
            </Button>
          </Box>
        </Grid>
      </Grid>
      {open && (
        <PromoDisplayConfigForm open={open} setOpen={setOpen} fetchData={fetchData} editableConfig={editableConfig} />
      )}

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={dataRows}
          columns={getColumns({
            handleExpirePromoConfig: handleExpirePromoConfigWithFetch,
            handleEditPromoDisplayConfig,
            handleClonePromoDisplayConfig,
            enqueueSnackbar,
            handlePromoDisplayNavigate,
          })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          rowHeight={200}
        />
      </Box>
    </>
  );
}
